/**
 * Prepare shared-chat assistant answers so covers + publication links
 * render like the main ChatBot (COVER → <a><img>, links_cover cards),
 * and expose GRAPH/EXCEL media for React embeds (not DOMPurify iframes).
 */

import { API_BASE_URL } from "configs/AppConfig";
import {
  extractSharedMedia,
  stripExcelLinksFromText,
  stripGraphLinksFromText,
} from "../utils/chatMediaUtils";

const escapeHtmlText = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeHtmlAttr = (text) => escapeHtmlText(text).replace(/"/g, "&quot;");

const isSpecialMarkdownLinkLabel = (label) => {
  const t = String(label || "")
    .trim()
    .toUpperCase();
  return t === "GRAPH" || t === "EXCEL" || t === "COVER";
};

const cleanMarkdownLinkUrl = (url) => {
  let cleanUrl = String(url || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/:\s*\/\s*\//g, "://");
  if (
    cleanUrl &&
    !cleanUrl.startsWith("http://") &&
    !cleanUrl.startsWith("https://") &&
    !cleanUrl.startsWith("/") &&
    !cleanUrl.startsWith("#") &&
    !cleanUrl.startsWith("mailto:")
  ) {
    cleanUrl = `https://${cleanUrl}`;
  }
  return cleanUrl;
};

const isValidCoverPath = (path) => {
  const s = String(path).trim().replace(/\s+/g, "");
  if (!s || s.length < 5) return false;
  if (/^COVER$/i.test(s) || /^[a-zA-Z]+$/.test(s)) return false;
  if (s.startsWith("http://") || s.startsWith("https://")) return true;
  if (
    s.startsWith("/") &&
    (s.includes("Publication") || s.includes("Image") || s.length > 3)
  ) {
    return true;
  }
  if (/\/Publication\/Image\//i.test(s)) return true;
  if (/[\w.-]+\.(png|jpe?g|gif|webp)(\?|$)/i.test(s)) return true;
  if (/^[a-f0-9-]{20,}(\.(png|jpe?g|gif|webp))?$/i.test(s)) return true;
  if (s.length >= 32 && /[a-f0-9-]{8,}/i.test(s)) return true;
  return false;
};

const resolveCoverSrc = (src) => {
  const s = String(src).trim().replace(/\s+/g, "");
  if (!s) return API_BASE_URL;
  if (/^https?:\/\/\//.test(s)) {
    return `${API_BASE_URL}/${s.replace(/^https?:\/\/\/+/, "")}`;
  }
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) {
    return s.includes("Publication") || s.includes("Image")
      ? `${API_BASE_URL}${s}`
      : `${API_BASE_URL}/Publication/Image${s}`;
  }
  return `${API_BASE_URL}/Publication/Image/${s.replace(/^\//, "")}`;
};

const coverLinkAttrs = (href) =>
  `href='${String(href).replace(
    /'/g,
    "&#39;",
  )}' target='_blank' rel='noopener noreferrer' class='shared-cover-link'`;

const coverImgTag = (href, imgSrc) =>
  `<a ${coverLinkAttrs(href)}><img class='shared-cover-img' src='${String(
    imgSrc,
  ).replace(
    /'/g,
    "&#39;",
  )}' alt='' style='max-width:150px;cursor:pointer;border-radius:8px;display:block;margin:20px 0 10px' /></a>`;

const normalizeHref = (u) => {
  const s = String(u)
    .trim()
    .replace(/\s+/g, "")
    .replace(/:\s*\/\s*\//g, "://");
  return s.startsWith("http") ? s : `https://${s}`;
};

const isImageUrl = (u) =>
  /\.(png|jpe?g|gif|webp)(\?|$)/i.test(u) ||
  /\/Publication\/Image\//i.test(u);

const usePubUrlForCover = (pubUrl) => {
  if (!pubUrl || isImageUrl(pubUrl)) return false;
  return true;
};

const fixBrokenUrl = (url) =>
  String(url || "")
    .replace(/https:\s+\/\//g, "https://")
    .replace(/http:\s+\/\//g, "http://")
    .replace(/:\s+(\d+)/g, ":$1");

/**
 * Extract links / links_cover dicts from answer text (same markers as ChatBot stream).
 */
export function extractLinksMetadata(answer) {
  let text = String(answer || "");
  let links = null;
  let linksCover = null;

  const parseDictBlock = (rawInner) => {
    const json = `{${rawInner}}`.replace(/'/g, '"').replace(/\n/g, "");
    const data = JSON.parse(json);
    Object.keys(data).forEach((key) => {
      if (data[key]) data[key] = fixBrokenUrl(data[key]);
    });
    return data;
  };

  const linksMatch = text.match(/links:\s*\{([\s\S]*?)\}/);
  if (linksMatch) {
    try {
      links = parseDictBlock(linksMatch[1]);
      text = text.replace(/links:\s*\{[\s\S]*?\}/g, "").trim();
    } catch {
      /* ignore malformed links block */
    }
  }

  const coversMatch = text.match(/links_cover:\s*\{([\s\S]*?)\}/);
  if (coversMatch) {
    try {
      linksCover = parseDictBlock(coversMatch[1]);
      text = text.replace(/links_cover:\s*\{[\s\S]*?\}/g, "").trim();
    } catch {
      /* ignore malformed covers block */
    }
  }

  // Drop trailing graph dict noise if present
  text = text.replace(/\{['"]graph['"]\s*:\s*['"][\s\S]*$/, "").trim();

  return { text, links, linksCover };
};

/**
 * Convert [COVER](...) + publication markdown links into ChatBot-like HTML.
 */
export function transformCoverMarkdown(input) {
  let processed = String(input || "");

  processed = processed.replace(/\[\s*C\s*OVER\s*\]/gi, "[COVER]");
  processed = processed.replace(/\[COVER\]\([^)]*$/g, "");

  // Pull cover/link lines out of leftover table pipes
  processed = processed.replace(/^\s*\|.+\|\s*$/gm, (match) => {
    const trimmed = match.trim();
    const isLinkOrCover = /\]\s*\(|\bCOVER\b|\[\s*C\s*OVER\s*\]/i.test(trimmed);
    if (!isLinkOrCover) return match;
    const inner = trimmed
      .replace(/^\|\s*/, "")
      .replace(/\s*\|$/, "")
      .trim();
    return `\n\n${inner}\n`;
  });

  processed = processed.replace(
    /\[COVER\]\(([^)]+)\)\s*\[([^\]]*)\]\(([^)]+)\)/gi,
    (m, imgUrl, name, sourceUrl) => {
      if (!isValidCoverPath(imgUrl)) {
        const href = normalizeHref(sourceUrl);
        return `<a ${coverLinkAttrs(href)}>${escapeHtmlText(
          (name || "").trim(),
        )}</a>`;
      }
      const imgSrc = resolveCoverSrc(imgUrl);
      const href = normalizeHref(sourceUrl);
      return coverImgTag(href, imgSrc);
    },
  );

  processed = processed.replace(
    /\[([^\]]*)\]\(([^)]+)\)\s*(?:<\s*br\s*\/?\s*>|\r?\n|\s)+\s*\[COVER\]\(([^)]+)\)/gi,
    (m, linkText, pubUrl, imgUrl) => {
      if (!usePubUrlForCover(pubUrl)) return m;
      const href = normalizeHref(pubUrl);
      const title = escapeHtmlText(linkText.trim());
      if (!isValidCoverPath(imgUrl)) {
        return `<a ${coverLinkAttrs(href)}>${title}</a>`;
      }
      const imgSrc = resolveCoverSrc(imgUrl);
      return `<a ${coverLinkAttrs(href)}>${title}</a>\n\n${coverImgTag(
        href,
        imgSrc,
      )}`;
    },
  );

  processed = processed.replace(
    /(\[[^\]]*\]\(([^)]+)\))([^|\[\]]*?)\[COVER\]\(([^)]+)\)/gi,
    (m, linkPart, pubUrl, between, imgUrl) => {
      if (!usePubUrlForCover(pubUrl)) return m;
      if (!isValidCoverPath(imgUrl)) return `${linkPart}${between}`;
      const href = normalizeHref(pubUrl);
      const imgSrc = resolveCoverSrc(imgUrl);
      return `${linkPart}${between}${coverImgTag(href, imgSrc)}`;
    },
  );

  processed = processed.replace(
    /\[COVER\]\(([^)]+)\)([^|\[\]]*?)(\[[^\]]*\]\(([^)]+)\))/gi,
    (m, imgUrl, between, linkPart, pubUrl) => {
      if (!usePubUrlForCover(pubUrl)) return m;
      if (!isValidCoverPath(imgUrl)) return `${between}${linkPart}`;
      const href = normalizeHref(pubUrl);
      const imgSrc = resolveCoverSrc(imgUrl);
      return `${coverImgTag(href, imgSrc)}${between}${linkPart}`;
    },
  );

  processed = processed.replace(
    /(\[[^\]]*\]\(([^)]+)\))\s*\|\s*\[COVER\]\(([^)]+)\)/gi,
    (m, linkPart, pubUrl, imgUrl) => {
      if (!usePubUrlForCover(pubUrl)) return m;
      if (!isValidCoverPath(imgUrl)) return `${linkPart} | `;
      const href = normalizeHref(pubUrl);
      const imgSrc = resolveCoverSrc(imgUrl);
      return `${linkPart} | ${coverImgTag(href, imgSrc)}`;
    },
  );

  processed = processed.replace(
    /\[COVER\]\(([^)]+)\)\s*\|\s*(\[[^\]]*\]\(([^)]+)\))/gi,
    (m, imgUrl, linkPart, pubUrl) => {
      if (!usePubUrlForCover(pubUrl)) return m;
      if (!isValidCoverPath(imgUrl)) return ` | ${linkPart}`;
      const href = normalizeHref(pubUrl);
      const imgSrc = resolveCoverSrc(imgUrl);
      return `${coverImgTag(href, imgSrc)} | ${linkPart}`;
    },
  );

  processed = processed.replace(/\[COVER\]\(([^)]+)\)/gi, (m, imgUrl) => {
    if (!isValidCoverPath(imgUrl)) return "";
    const imgSrc = resolveCoverSrc(imgUrl);
    return coverImgTag(imgSrc, imgSrc);
  });

  // Normal markdown links → HTML anchors (skip COVER/GRAPH/EXCEL)
  processed = processed.replace(
    /\[([^\]]+)\]\s*(?:<\s*br\s*\/?>|\n|\r\n|\s)*\(\s*([^)]*)\s*\)/gi,
    (match, linkText, url) => {
      const label = String(linkText).trim();
      if (!label || isSpecialMarkdownLinkLabel(label)) return match;
      const href = cleanMarkdownLinkUrl(url);
      if (!href) return match;
      return `<a href="${escapeHtmlAttr(
        href,
      )}" target="_blank" rel="noopener noreferrer" dir="auto" class="shared-pub-link">${escapeHtmlText(
        label,
      )}</a>`;
    },
  );

  // Normalize broken spaced URLs in HTML attrs
  const normalizeHtmlAttrUrl = (rawUrl) =>
    String(rawUrl || "")
      .trim()
      .replace(/:\s*\/\s*\//g, "://")
      .replace(/\s+/g, "");
  processed = processed.replace(
    /(href|src)\s*=\s*"([^"]*)"/gi,
    (_, attr, url) => `${attr}="${normalizeHtmlAttrUrl(url)}"`,
  );
  processed = processed.replace(
    /(href|src)\s*=\s*'([^']*)'/gi,
    (_, attr, url) => `${attr}='${normalizeHtmlAttrUrl(url)}'`,
  );

  // Soften newlines for HTML display (avoid double-breaking already HTML answers)
  processed = processed.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n");
  const looksLikeHtml = /<\/?(?:p|div|table|ul|ol|li|h[1-6]|br|a|img|span)\b/i.test(
    processed,
  );
  if (!looksLikeHtml) {
    processed = processed.replace(/\n/g, "<br />");
  } else {
    processed = processed.replace(/\n{2,}/g, "<br /><br />");
  }

  return processed;
}

/**
 * Full pipeline for a shared answer string.
 */
export function prepareSharedAnswer(answer, extra = {}) {
  const extracted = extractLinksMetadata(answer);
  const links = extra.links || extracted.links;
  const linksCover =
    extra.linksCover || extra.links_cover || extracted.linksCover;

  const mediaInfo = extractSharedMedia(extracted.text);
  const textForHtml = stripExcelLinksFromText(
    stripGraphLinksFromText(mediaInfo.textWithoutMedia || extracted.text),
  );
  const html = transformCoverMarkdown(textForHtml);

  // Prefer ordered inline media; fall back to aggregated URL lists
  let media = Array.isArray(mediaInfo.media)
    ? mediaInfo.media.filter((item) => item.type === "graph" || item.type === "excel")
    : [];

  if (!media.length) {
    media = [
      ...mediaInfo.graphUrls.map((url) => ({ type: "graph", url })),
      ...mediaInfo.excelUrls.map((url) => ({ type: "excel", url })),
    ];
  }

  // Structured fields from share API (if provided)
  const graphHtml = extra.graph || extra.graphHtml || null;
  if (extra.excelUrls && Array.isArray(extra.excelUrls)) {
    extra.excelUrls.forEach((url) => {
      if (url && !media.some((m) => m.type === "excel" && m.url === url)) {
        media.push({ type: "excel", url });
      }
    });
  }

  return {
    html,
    links,
    linksCover,
    media,
    graphUrls: mediaInfo.graphUrls,
    excelUrls: mediaInfo.excelUrls,
    graphHtml,
  };
}
